import React, { useState, useEffect } from "react";
import {
  Col,
  Container,
  Row,
  FormGroup,
  Button,
  Card,
  Label,
  Input,
  Progress,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { post, put, get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { toast, Bounce } from "react-toastify";
import imgupload from "../../../assets/images/imageupload.svg";

const EditEquipment = () => {
  document.title = "Edit Equipment | Elevated - Admin Dashboard";
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState({});
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const equipFromParams = searchParams.get("admin");
  const [adminStatus, setAdminStatus] = useState(
    searchParams.get("admin") ? true : false
  );
  const { id } = useParams();
  const [brandData, setBrandData] = useState([]);
  const getCat = async () => {
    try {
      // Use Promise.all to handle all uploads
      const resp = await get(`${endpoints.brand}`);
      if (resp.data.success) {
        setBrandData(resp.data.data);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };
  useEffect(() => {
    getCat();
  }, []);
  useEffect(() => {
    fetchEquipmentDetails();
  }, [id]);

  const fetchEquipmentDetails = async () => {
    try {
      const response = await get(`${endpoints.equip}/${id}`);

      if (response?.data?.data) {
        const equipData = response.data.data;

        setEquipment({
          name: equipData.name || "",
          brand: equipData.brand.id || "",
          category: equipData.category || "",
          file: equipData?.file || "",
          gymId: equipData?.gym?.id || "",
        });
      } else {
        toast.error("No data found for the provided equipment ID.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      }
    } catch (error) {
      console.error("Error fetching equipment details:", error);
      toast.error("Failed to fetch equipment details. Please try again.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const SUPPORTED_FORMATS = ["image/jpeg", "image/png", "image/jpg"];
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters"),
    brand: Yup.string().required("Brand is required"),

    category: Yup.string()
      .required("Category is required")
      .min(2, "Category must be at least 2 characters"),

    file: Yup.mixed().test(
      "file-validation",
      "File is required and must be an image (JPEG, PNG)",
      (value) => {
        // Skip validation if `file` is a string
        if (typeof value === "string") return true;

        // Validate if `file` is a File and is an image
        return (
          value &&
          value instanceof File &&
          SUPPORTED_FORMATS.includes(value.type)
        );
      }
    ),
  });

  const handleSubmit = async (values) => {
    console.log("I am submitting", values);

    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("brand", values.brand);
    formData.append("category", values.category);
    if (typeof values.file != "string" && values.file != null) {
      formData.append("file", values.file);
    }
    formData.append("gymId", values.gymId);

    const resp = await put(`${endpoints.equip}/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (resp?.data?.success) {
      toast.success(`Equipments Updated Successfully`, {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      });
      navigate(
        `/equip-listing?gymId=${values.gymId}${
          adminStatus ? "&admin=true" : ""
        }`
      );
    }
  };

  return (
    <div className="mx-2">
      <Container fluid className="">
        <Row>
          <Card className="rounded-4 ">
            <Formik
              initialValues={equipment}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({
                errors,
                touched,
                resetForm,
                values,
                setFieldValue,
                isSubmitting,
              }) => (
                <Form className="p-4">
                  <h3 className="primary-black ff-sora font-size-20 fw-bold">
                    Edit Workout Equipment
                  </h3>
                  <div className="row align-items-center">
                    <FormGroup className="col-12 col-sm-auto">
                      <div
                        className="cursor-pointer overflow-hidden position-relative ff-sora font-size-16 fw-normal secondary-black rounded-5 bg-light d-flex flex-column justify-content-center align-items-center"
                        style={{
                          border: "1px dashed #ccc",
                          width: "160px",
                          height: "160px",
                        }}
                      >
                        {values.file instanceof File ? (
                          <img
                            src={URL?.createObjectURL(values?.file)}
                            alt="Upload"
                            className="h-100 w-100 object-fit-cover"
                          />
                        ) : values.file != null &&
                          typeof values.file == "string" ? (
                          <img
                            src={`${process.env.REACT_APP_IMG_URL}${values.file}`}
                            alt="Upload"
                            className="h-100 w-100 object-fit-cover cursor-pointer"
                          />
                        ) : (
                          <>
                            <img
                              src={imgupload}
                              alt="Upload"
                              height="40px"
                              width="40px"
                            />
                            <div className="secondary-black text-center mt-2 ff-sora font-size-16 fw-normal">
                              Upload Media
                            </div>
                          </>
                        )}

                        <input
                          type="file"
                          accept="image/*"
                          className="position-absolute top-0"
                          style={{
                            width: "100%",
                            height: "100%",
                            opacity: 0,
                          }}
                          onChange={(e) => {
                            setFieldValue(`file`, e.target.files[0]);
                          }}
                        />
                      </div>
                      <ErrorMessage
                        name={`file`}
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>

                    <FormGroup className="col-12 col-sm-6 col-md-4 col-lg-3">
                      <Field
                        style={{
                          width: "100%",
                          maxWidth: "412px",
                          height: "56px",
                          border: "none",
                        }}
                        name={`name`}
                        as={Input}
                        placeholder="Equipment Name"
                        className={`rounded-5 bg-light custom-placeholder ff-sora font-size-16 fw-normal px-4  ${
                          errors?.name && touched?.name ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name={`name`}
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                    <FormGroup className="col-12 col-sm-6 col-md-4 col-lg-3">
                      <Field
                        as={Input}
                        type="select"
                        name={`brand`}
                        className={`rounded-5 bg-light custom-placeholder ff-sora font-size-16 fw-normal px-4 ${
                          errors.brand && touched.brand ? "is-invalid" : ""
                        }`}
                        style={{
                          width: "100%",
                          maxWidth: "412px",
                          height: "56px",
                          border: "none",
                        }}
                      >
                        <option value="">Select Brand</option>
                        {brandData.map((brand) => (
                          <option key={brand.id} value={brand?.id}>
                            {brand?.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name={`brand`}
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>

                    <FormGroup className="col-12 col-sm-6 col-md-4 col-lg-3">
                      <Field
                        style={{
                          width: "100%",
                          maxWidth: "412px",
                          height: "56px",
                          border: "none",
                        }}
                        name={`category`}
                        as={Input}
                        type="select"
                        className={`rounded-5 bg-light custom-placeholder ff-sora font-size-16 fw-normal px-4  ${
                          errors?.category && touched?.category
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Category</option>
                        <option value="Cardio">Cardio</option>
                        <option value="Strength">Strength</option>
                        <option value="Flexibility">Flexibility</option>
                      </Field>
                      <ErrorMessage
                        name={`category`}
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </div>

                  <div className="d-flex float-end">
                    <Button
                      type="submit"
                      className="me-2 rounded-pill"
                      disabled={isSubmitting}
                    >
                      Update Equipment
                    </Button>
                    <Button
                      color="white"
                      className="rounded-pill1"
                      onClick={() => {
                        resetForm();

                        navigate(`/equip-listing?gymId=${values.gymId}`);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card>
        </Row>
      </Container>
    </div>
  );
};

export default EditEquipment;
